import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import usePageMeta from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, ArrowRight, MapPin, Clock3, CheckCircle2 } from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import { LOCATIONS, SERVICES, PHONES, WHATSAPP_URL } from "@/constants/content";

export default function LocationDetailPage() {
  const { slug } = useParams();
  const { open } = useBooking();
  const loc = LOCATIONS.find((l) => l.slug === slug);
  usePageMeta(
    loc ? `Home Healthcare in ${loc.name} — KinFlex` : "Location — KinFlex",
    loc ? `Trusted home nursing, ICU setup, physiotherapy & elder care in ${loc.name}. ${loc.tagline}` : ""
  );
  if (!loc) return <Navigate to="/locations" replace />;

  return (
    <Layout>
      <section className="pt-28 pb-10 md:pt-36 md:pb-16 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative">
          <div className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0A84FF]">Home</Link> ·{" "}
            <Link to="/locations" className="hover:text-[#0A84FF]">Locations</Link> ·{" "}
            <span className="text-slate-700">{loc.name}</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
                <MapPin className="h-3.5 w-3.5" style={{ color: '#0A84FF' }} />
                <span className="text-xs font-semibold text-slate-700">Home Healthcare in {loc.name}</span>
              </div>
              <h1 className="font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-[52px] tracking-tight leading-[1.05]">
                Trusted home healthcare in <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">{loc.name}</span>.
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">{loc.tagline}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <Clock3 className="h-3.5 w-3.5" style={{ color: '#10B981' }} /> Response: {loc.responseTime}
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <MapPin className="h-3.5 w-3.5" style={{ color: '#0A84FF' }} /> Pincodes: {loc.pincodes}
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button onClick={() => open()} className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-7 h-12 font-semibold shadow-lg shadow-[#0A84FF]/25" data-testid="location-book-btn">
                  Book Appointment <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <a href={`tel:${PHONES[0]}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-900 px-6 h-12 font-semibold">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call Now
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white px-6 h-12 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 premium-shadow">
              <img src={loc.image} alt={loc.name} className="w-full h-[380px] md:h-[460px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="eyebrow mb-3">About the service area</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl tracking-tight">
              Home healthcare across every {loc.name} locality.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">{loc.description}</p>

            <div className="mt-10">
              <h3 className="font-display font-semibold text-slate-900 text-xl">Coverage</h3>
              <p className="mt-2 text-sm text-slate-600">{loc.coverage}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {loc.landmarks.map((lm) => (
                  <span key={lm} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F8FAFC] border border-slate-200 text-xs font-medium text-slate-700">
                    <MapPin className="h-3 w-3" style={{ color: '#0A84FF' }} /> {lm}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h3 className="font-display font-semibold text-slate-900 text-xl">All services available in {loc.name}</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-start gap-2.5 rounded-xl border border-slate-200/70 bg-[#F8FAFC] hover:border-[#0A84FF]/30 hover:bg-white transition-all p-3">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#10B981' }} />
                      <span className="text-sm text-slate-700">{s.title} in {loc.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start">
            <div className="rounded-3xl bg-slate-900 text-white p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: '#4dabff' }}>Fast Response</div>
              <h3 className="mt-3 font-display font-bold text-2xl leading-tight">
                Nurses reach {loc.name} in {loc.responseTime.toLowerCase()}.
              </h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">Book now and our care coordinator will call within minutes.</p>
              <div className="mt-6 space-y-2.5">
                <Button onClick={() => open()} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-11 font-semibold">
                  Book Appointment
                </Button>
                <a href={`tel:${PHONES[0]}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 h-11 font-semibold">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call {PHONES[0]}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white h-11 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
